import { ReactElement, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import { RootState } from 'src/store/reducers';

import axios, { AxiosRequestConfig, AxiosResponse, AxiosPromise } from 'axios';

import { OfficeInformations, UserEyeExam } from '@interfaces/myAppointmentInterfaces';

import { getLabelFallback } from '@utils/utils';

import InfoIcon from '@images/svg-icons/infoIcon.svg';
import CheckGreen from '@images/svg-icons/checkGreen.svg';
import BookedAppIcon from '@images/svg-icons/bookedAppIcon.svg';


interface Props {
    eyeExam: UserEyeExam;
    openDeleteModal: (schedId: number) => void;
    openDoctorInfoModal: (schedId: number, providerId: number) => void;
    isUpcoming: boolean;
}

interface DifResponse {
    appointmentId: string;
    appointmentStatus: string;
    formStatus: string;
}

const getDifStatus = (
    isMockRequest: boolean,
    difEndPoint: string,
    difKey: string,
    encApptId: string
): AxiosPromise<DifResponse> => {
    if (isMockRequest) {
        return new Promise<AxiosResponse<DifResponse>>((resolve, reject) => {
            resolve({
                data: {
                    formStatus: encApptId.includes('999') ? 'COMPLETED' : 'NOPE',
                    appointmentId: encApptId,
                    appointmentStatus: "ACTIVE"
                },
                status: 200,
                statusText: 'OK',
                headers: {},
                config: {},
            });
        });
    } else {
        const config: AxiosRequestConfig = {
            method: 'GET',
            url: difEndPoint.replace('{encApptId}', encApptId),
            headers: {
                'Content-Type': 'application/json',
                'Ocp-Apim-Subscription-Key': difKey,
                caller: 'rona',
            },
        };

        return axios(config);
    }
};

const EyeExamCard = ({ eyeExam, openDeleteModal, openDoctorInfoModal, isUpcoming }: Props): ReactElement => {
    const config = useSelector((state: RootState) => state.common.config);
    const isMobile = useSelector((state: RootState) => state.common.isMobile);

    const difEndpoint = config.callFromComponentToTO.myAppointments.eyeExams.myAccDifEndPoint;
    const difKey = config.callFromComponentToTO.myAppointments.eyeExams.myAccDifKey;
    const isOEERedesign2025WidgetEnabled = config.isOEERedesign2025WidgetEnabled;

    const [difStatus, setDifStatus] = useState<string>(null);

    useEffect(() => {
        if (eyeExam.encApptId && eyeExam.difEnabled) {
            getDifStatus(config.mockRequest, difEndpoint, difKey, eyeExam.encApptId).then( //for mock"4d467051516c6f784e6c513557456452536a5a614d44426155454a614d545a554f56684855556f32576a424279563832476665386f687671377071304b72454b32392f6d44687872437a3541" 
                response => {
                    console.log("respose stautus", response?.data[0]?.formStatus, response?.data)
                    setDifStatus(response?.data[0]?.formStatus);
                }
            );
        }
    }, []);

    /** Get the office full address */

    const getOfficeFullAddress = (officeInformations: OfficeInformations): string => {
        return `${officeInformations.address}, ${officeInformations.city}, ${officeInformations.state}, ${officeInformations.postalCode}`;
    };

    const getOfficeFEAddress = (officeInformations: OfficeInformations): JSX.Element => {
        return (
            <>
                <div className='address-wrap'>
                    <div className='part1'>{officeInformations?.name}, {officeInformations?.address}</div>
                    <div className='part2'>{officeInformations?.address2}</div>
                    <div className='part3'>{officeInformations?.city}, {officeInformations?.state} {officeInformations?.postalCode}</div>

                </div>
            </>
        );
    };

    /** Reschedule/cancel appointment page */

    const goToScheduleDateExamViewPage = (appointmentId: number, storeNumber: string) => {
        window.open(
            config.callFromComponentToTO.myAppointments.eyeExams.rescheduleEyeExam
                .replace('{storeNumber}', storeNumber)
                .replace('{catalogId}', config.catalogId)
                .replace('{langId}', config.langId)
                .replace('{appointmentId}', appointmentId)
                .replace('{storeId}', config.storeId),
            '_parent'
        );
    };

    const goToSchedulePatientExamViewPage = (appointmentId: number, storeNumber: string) => {
        window.open(
            config.callFromComponentToTO.myAppointments.eyeExams.editEyeExam
                .replace('{storeNumber}', storeNumber)
                .replace('{catalogId}', config.catalogId)
                .replace('{langId}', config.langId)
                .replace('{appointmentId}', appointmentId)
                .replace('{storeId}', config.storeId),
            '_parent'
        );
    };

    const goToCancelExamViewPage = (appointmentId: number, storeNumber: string) => {
        window.open(
            config.callFromComponentToTO.myAppointments.eyeExams.cancelEyeExam
                .replace('{storeNumber}', storeNumber)
                .replace('{catalogId}', config.catalogId)
                .replace('{langId}', config.langId)
                .replace('{appointmentId}', appointmentId)
                .replace('{storeId}', config.storeId),
            '_parent'
        );
    };

    /**TBD with functional analyst */

    const goToDigitalIntakeForm = (encApptId: string) => {
        window.open(
            config.callFromComponentToTO.myAppointments.eyeExams.eyeExamFormsOnline.replace(
                '{encApptId}',
                encApptId
            ),
            '_blank'
        );
    };

    const renderWrapperHeader = (eyeExam: UserEyeExam): JSX.Element => {
        //eyeExam.status = 'CONFIRM' // vincenzo eliminare
        const { schedId, date, time, office, status, isChangeAllowed } = eyeExam;
        const pattern = /,\s(\d{4})/;
        const formattedDate = date.replace(pattern, ' $1');
        return (
            <div
                role="region"
                aria-label="eye exams details date"
                className="wrapper-summary-header"
            >
                <div className="wrapper-summary-header-date">
                    {getLabelFallback('MYAPPOINTMENTS_EYE_EXAMS_UPCOMING_DATE_LABEL')
                        .replace('{EYE_EXAM_DATE}', formattedDate.toUpperCase())
                        .replace('{EYE_EXAM_HOUR}', time.toUpperCase())}
                </div>
                {!!status && isUpcoming && ["CANCEL", "COMPLETE", "NEE", "CONFIRM"].includes(status) && (
                    <div className='wrapper-summary-header-status'>
                        <div className='icon-status'>
                            {!!status && !status.includes("CANCEL") && (
                                <BookedAppIcon />
                            )}
                        </div>
                        <div className='description-status'>
                            {!!status && status.includes("CANCEL") ? (
                                <span>{getLabelFallback('MYAPPOINTMENTS_EYE_EXAMS_UPCOMING_STATUS_CANCELLED')}</span>
                            ) : (
                                <span>{getLabelFallback('MYAPPOINTMENTS_EYE_EXAMS_UPCOMING_STATUS_BOOKED')}</span>
                            )}
                        </div>
                    </div>
                )}


                {!isMobile && isChangeAllowed && (
                    <div
                        role="region"
                        aria-label="eye exams details buttons"
                        className="wrapper-summary-header-buttons"
                    >
                        <button
                            className="primary-button-negative black button-size-small"
                            data-element-id="Account_BookAnAppointment"
                            data-description="Cancel"
                            onClick={() => {
                                isOEERedesign2025WidgetEnabled && isOEERedesign2025WidgetEnabled === 'true'
                                    ? goToCancelExamViewPage(schedId, office.externalIdentifier)
                                    : openDeleteModal(schedId)
                            }}
                        >
                            {getLabelFallback('MY_APPOINTMENTS_EYE_EXAMS_UPCOMING_CANCEL_EXAM')}
                        </button>
                        <button
                            className="primary-button black button-size-small"
                            data-element-id="Account_BookAnAppointment"
                            data-description="Reschedule"
                            onClick={() =>
                                goToScheduleDateExamViewPage(schedId, office.externalIdentifier)
                            }
                        >
                            {getLabelFallback('MY_APPOINTMENTS_EYE_EXAMS_UPCOMING_RESCHEDULE')}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderWrapperBody = (eyeExam: UserEyeExam): JSX.Element => {
        const { isChangeAllowed, patient, provider, office, schedId } = eyeExam;
        if (!!office && !!office.name) {
            let newName = office.name.replace(/^TG\w*\s*/, "");
            office.name = newName;
        }
        return (
            <div className="wrapper-summary-content">
                <div className="details location">
                    {office && (
                        <>
                            <div
                                role="region"
                                aria-label="eye exams details office address label"
                                className="wrapper-summary-content-label"
                            >
                                {getLabelFallback(
                                    'MY_APPOINTMENTS_EYE_EXAMS_UPCOMING_LOCATION_LABEL'
                                )}
                            </div>
                            <div
                                role="region"
                                aria-label="eye exams details office address value"
                                className="wrapper-summary-content-value"
                            >
                                <div className="address">{getOfficeFEAddress(office)}</div>
                                <div className="contacts">
                                    {office?.phone && (
                                        <a
                                            href={`tel:${office.phone}`}
                                            data-analytics_available_call="0"
                                        >
                                            {office.phone}
                                        </a>
                                    )}
                                    {office && (
                                        <a
                                            data-analytics_available_call="0"
                                            href={`http://maps.google.com/?q=${getOfficeFullAddress(
                                                office
                                            )}`}
                                            target="_blank"
                                        >
                                            {getLabelFallback(
                                                'MY_APPOINTMENTS_EYE_EXAMS_UPCOMING_DIRECTION_LABEL'
                                            )}
                                        </a>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="details patient">
                    {patient && (
                        <>
                            <div
                                role="region"
                                aria-label="eye exams details patient label"
                                className="primary-message medium bold align-left wrapper-summary-content-label"
                            >
                                {getLabelFallback(
                                    'MY_APPOINTMENTS_EYE_EXAMS_UPCOMING_PATIENT_LABEL'
                                )}
                            </div>
                            <div
                                role="region"
                                aria-label="eye exams details patient value"
                                className="primary-message medium align-right wrapper-summary-content-value"
                            >
                                {`${patient.firstName} ${patient.lastName}`}
                                {isChangeAllowed && (
                                    <a
                                        role="region"
                                        aria-label="eye exams details patient button"
                                        className="edit-patient"
                                        data-analytics_available_call="0"
                                        onClick={() =>
                                            goToSchedulePatientExamViewPage(
                                                schedId,
                                                office.externalIdentifier
                                            )
                                        }
                                    >
                                        {isMobile && (<br></br>)}
                                        {getLabelFallback('EDIT_CTA_LABEL')}
                                    </a>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="details doctor">
                    {provider && (
                        <>
                            <div
                                role="region"
                                aria-label="eye exams details doctor label"
                                className="primary-message medium bold align-left wrapper-summary-content-label "
                            >
                                {getLabelFallback(
                                    'MY_APPOINTMENTS_EYE_EXAMS_UPCOMING_DOCTOR_LABEL'
                                )}
                            </div>
                            <div
                                role="region"
                                aria-label="eye exams details doctor value"
                                className="primary-message medium align-left wrapper-summary-content-value"
                            >
                                {provider}

                                <a
                                    role="region"
                                    aria-label="eye exams details doctor info button"
                                    className="doctor-info"
                                    data-analytics_available_call="0"
                                    onClick={() => {
                                        openDoctorInfoModal(eyeExam.office.externalIdentifier, eyeExam.providerId);
                                    }}
                                >
                                    {isMobile && (<br></br>)}
                                    {getLabelFallback(
                                        'MY_APPOINTMENTS_EYE_EXAMS_UPCOMING_DOCTOR_INFO_LABEL'
                                    )}
                                </a>
                            </div>
                        </>
                    )}
                </div>

            </div>
        );
    };

    const renderDifMessage = (eyeExam: UserEyeExam): JSX.Element | null => {
        if (!(eyeExam.isChangeAllowed && eyeExam.difEnabled && eyeExam.encApptId && isUpcoming)) return;


        return (
            <>

                {difStatus === 'COMPLETED' && (
                    <div
                        role="region"
                        aria-label="eye exams details footer link"
                        className="wrapper-summary-footer dif-completed"
                    >
                        {isMobile ? (
                            <CheckGreen height="16" width="16" viewBox="0 0 24 24" />
                        ) : (
                            <CheckGreen />
                        )}
                        <div className="primary-message medium align-left">
                            {getLabelFallback(
                                'MY_APPOINTMENTS_EYE_EXAMS_UPCOMING_COMPLETED_LABEL_FIRST_PART'
                            )}
                            {isMobile && (<br></br>)}
                            <span className='green-massage-dif'>
                                {getLabelFallback(
                                    'MY_APPOINTMENTS_EYE_EXAMS_UPCOMING_COMPLETED_LINK_LABEL'
                                )}
                            </span>
                            <span className='green-massage-dif'>
                                {getLabelFallback('MY_APPOINTMENTS_EYE_EXAMS_UPCOMING_COMPLETED_LABEL_SECOND_PART')}
                            </span>
                        </div>
                    </div>
                )}
                {["PENDING", "NOTSTARTED ", "OPEN "].includes(difStatus) && (
                    <div
                        role="region"
                        aria-label="eye exams details footer link"
                        className="wrapper-summary-footer"
                    >
                        {isMobile ? (
                            <InfoIcon />
                        ) : (
                            <InfoIcon height="24" width="24" viewBox="0 0 16 16" />
                        )}
                        <div className="primary-message medium align-left">
                            {getLabelFallback(
                                'MY_APPOINTMENTS_EYE_EXAMS_UPCOMING_REMINDER_LABEL_FIRST_PART'
                            )}
                            {
                                <a onClick={() => goToDigitalIntakeForm(eyeExam.encApptId)}>
                                    {getLabelFallback(
                                        'MY_APPOINTMENTS_EYE_EXAMS_UPCOMING_REMINDER_LINK_LABEL'
                                    )}
                                </a>
                            }
                            {getLabelFallback(
                                'MY_APPOINTMENTS_EYE_EXAMS_UPCOMING_REMINDER_LABEL_SECOND_PART'
                            )}
                        </div>
                    </div>
                )}
            </>
        );
    };

    const renderWrapperFooter = (eyeExam: UserEyeExam): JSX.Element | null => {
        return (
            <>
                {renderDifMessage(eyeExam)}

                {isMobile && eyeExam.isChangeAllowed && (
                    <>
                        <hr />
                        <div
                            role="region"
                            aria-label="eye exams details buttons"
                            className="wrapper-summary-header-buttons"
                        >
                            <button
                                className="primary-button black button-size-medium"
                                data-element-id="Account_BookAnAppointment"
                                data-description="Reschedule"
                                onClick={() =>
                                    goToScheduleDateExamViewPage(
                                        eyeExam.schedId,
                                        eyeExam.office.externalIdentifier
                                    )
                                }
                            >
                                {getLabelFallback('MY_APPOINTMENTS_EYE_EXAMS_UPCOMING_RESCHEDULE')}
                            </button>
                            <button
                                className="primary-button-negative black button-size-medium"
                                data-element-id="Account_BookAnAppointment"
                                data-description="Cancel"
                                onClick={() => {
                                    isOEERedesign2025WidgetEnabled && isOEERedesign2025WidgetEnabled === 'true'
                                        ? goToCancelExamViewPage(eyeExam.schedId, eyeExam.office.externalIdentifier)
                                        : openDeleteModal(eyeExam.schedId)
                                }}
                            >
                                {getLabelFallback('MY_APPOINTMENTS_EYE_EXAMS_UPCOMING_CANCEL_EXAM')}
                            </button>
                        </div>
                    </>
                )}
            </>
        );
    };

    return (
        <div className="wrapper-summary" key={eyeExam.schedId}>
            {renderWrapperHeader(eyeExam)}
            <hr />
            {renderWrapperBody(eyeExam)}
            {renderWrapperFooter(eyeExam)}
        </div>
    );
};
export default EyeExamCard;
